import React, { useState, useEffect } from "react";
import { 
  Calendar, 
  Zap, 
  RefreshCw, 
  CheckCircle,
  XCircle
} from "lucide-react";
// import axios from "axios";
// import { ENV } from "../../utils/env.js";

const CheckLicenseStatus = () => {
  const [licenseData, setLicenseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchLicenseStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Dummy data that matches the actual API response structure
      const dummyData = {
        client: {
          clientName: "Acme Corporation",
          daysLeft: 245,
          subscriptionStarts: "2024-01-15T00:00:00Z",
          subscriptionEnds: "2025-09-15T00:00:00Z",
          services: {
            github_repo_scans: true,
            github_repo_scans_limit: 500,
            bitbucket_repo_scans: true,
            bitbucket_repo_scans_limit: 200,
            gitlab_repo_scans: true,
            gitlab_repo_scans_limit: 150,
            github_ci_gating: true,
            github_ci_gating_limit: 1000,
            bitbucket_ci_gating: true,
            bitbucket_ci_gating_limit: 500,
            gitlab_ci_gating: true,
            gitlab_ci_gating_limit: 300,
            s3_scans: true,
            s3_scans_limit: 250,
            iam_roles_scans: true,
            iam_roles_scans_limit: 100,
            slack_alerts: true,
            slack_alerts_limit: 0,
            slack_history: true,
            slack_history_limit: 0,
            slack_history_channel_limit: 15,
            jira_enabled: true,
            jira_enabled_limit: 0,
            freshworks: false,
            freshworks_limit: 0,
            vault: true,
            vault_limit: 0,
            ai_rem: true,
            ai_rem_limit: 100,
            anomization_enabled: true,
            anomization_enabled_limit: 0,
            anomization_show_limit: 50
          },
          services_usage: {
            github_repo_scans_limit: 342,
            bitbucket_repo_scans_limit: 87,
            gitlab_repo_scans_limit: 43,
            github_ci_gating_limit: 756,
            bitbucket_ci_gating_limit: 234,
            gitlab_ci_gating_limit: 156,
            s3_scans_limit: 128,
            iam_roles_scans_limit: 67,
            slack_history_channel_limit: 12,
            ai_rem_limit: 45
          }
        }
      };
      
      setLicenseData(dummyData);
    } catch (err) {
      console.error("Error fetching license status:", err);
      setError("Failed to fetch license status");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchLicenseStatus();
    setRefreshing(false);
  };

  useEffect(() => {
    fetchLicenseStatus();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };


  const renderUsageText = (used, limit) => {
    if (limit === 0) return "No limit";
    const percentage = Math.round((used / limit) * 100);
    return `${used}/${limit} (${percentage}%)`;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-6 w-32 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 w-48 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="h-8 w-8 bg-gray-200 rounded-full animate-pulse" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[1, 2].map((i) => (
            <div key={i} className="bg-gray-50 p-3 rounded border">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
              </div>
              <div className="space-y-1">
                <div className="h-3 w-32 bg-gray-200 rounded animate-pulse" />
                <div className="h-3 w-28 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-3">
          <div className="h-5 w-48 bg-gray-200 rounded animate-pulse" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-gray-50 p-2 rounded border">
                <div className="flex items-center justify-between mb-1">
                  <div className="h-3 w-20 bg-gray-200 rounded animate-pulse" />
                  <div className="h-2 w-2 bg-gray-200 rounded-full animate-pulse" />
                </div>
                <div className="h-3 w-16 bg-gray-200 rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-3">
          <div className="h-5 w-36 bg-gray-200 rounded animate-pulse" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-gray-50 p-2 rounded border">
                <div className="flex items-center justify-between">
                  <div className="h-3 w-18 bg-gray-200 rounded animate-pulse" />
                  <div className="h-2 w-2 bg-gray-200 rounded-full animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <p className="text-red-600">{error}</p>;
  }

  if (!licenseData) {
    return <p className="text-gray-500">No license data available.</p>;
  }

  const { client } = licenseData;
  
  // All services list
  const allServices = [
    { key: 'github_repo_scans', name: 'GitHub Repos' },
    { key: 'bitbucket_repo_scans', name: 'Bitbucket Repos' },
    { key: 'gitlab_repo_scans', name: 'GitLab Repos' },
    { key: 'github_ci_gating', name: 'GitHub CI' },
    { key: 'bitbucket_ci_gating', name: 'Bitbucket CI' },
    { key: 'gitlab_ci_gating', name: 'GitLab CI' },
    { key: 's3_scans', name: 'S3 Storage' },
    { key: 'iam_roles_scans', name: 'IAM Roles' },
    { key: 'slack_alerts', name: 'Slack Alerts' },
    { key: 'slack_history', name: 'Slack History' },
    { key: 'jira_enabled', name: 'Jira' },
    { key: 'freshworks', name: 'Freshworks' },
    { key: 'vault', name: 'Vault' },
    { key: 'ai_rem', name: 'AI Remediation' },
    { key: 'anomization_enabled', name: 'Anonymization' }
  ];

  const getServiceUsage = (serviceKey) => {
    const used = client?.services_usage?.[`${serviceKey}_limit`] ?? 0;
    const limit = client?.services?.[`${serviceKey}_limit`] ?? 0;
    const isEnabled = client?.services?.[serviceKey] ?? false;
    return { used, limit, isEnabled };
  };

  const getSpecialLimits = () => {
    return {
      anomization_show_limit: client?.services?.anomization_show_limit ?? 0,
      slack_history_channel_limit: client?.services?.slack_history_channel_limit ?? 0,
      slack_history_channel_used: client?.services_usage?.slack_history_channel_limit ?? 0
    };
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">License Status</h3>
          <p className="text-sm text-gray-600">Client: {client.clientName} • {client.daysLeft} days remaining</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="p-1 rounded hover:bg-gray-100 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 text-gray-700 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Summary Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Subscription Info */}
        <div className="bg-gray-50 p-3 rounded border">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-gray-600" />
            <h4 className="text-sm font-semibold text-gray-800">Subscription</h4>
          </div>
          <div className="text-xs text-gray-600 justify-between flex">
            <div>Started: {formatDate(client.subscriptionStarts)}</div>
            <div>Ends: {formatDate(client.subscriptionEnds)}</div>
          </div>
        </div>

        <div className="bg-gray-50 p-3 rounded border">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-gray-600" />
            <h4 className="text-sm font-semibold text-gray-800">Active Services</h4>
          </div>
          <div className="text-xs text-gray-600">
            {Object.values(client.services).filter(Boolean).length} of {Object.keys(client.services).length} enabled
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Services with Usage Limits</h3>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {allServices.map((service) => {
            const { used, limit, isEnabled } = getServiceUsage(service.key);
            if (limit === 0) return null;
            
            return (
              <div key={service.key} className="bg-gray-50 p-2 rounded border text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-800 truncate">
                    {service.name}
                  </span>
                  {isEnabled ? (
                    <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-3 h-3 text-red-500 flex-shrink-0" />
                  )}
                </div>
                <div className="text-gray-600">
                  {renderUsageText(used, limit)}
                </div>
              </div>
            );
          })}
          
          {(() => {
            const specialLimits = getSpecialLimits();
            const specialServices = [];
            
            if (specialLimits.anomization_show_limit > 0) {
              specialServices.push({
                key: 'anomization_show_limit',
                name: 'Anonymization Show',
                used: 0,
                limit: specialLimits.anomization_show_limit,
                isEnabled: true
              });
            }
            
            if (specialLimits.slack_history_channel_limit > 0) {
              specialServices.push({
                key: 'slack_history_channel_limit',
                name: 'Slack Channels',
                used: specialLimits.slack_history_channel_used,
                limit: specialLimits.slack_history_channel_limit,
                isEnabled: true
              });
            }
            
            return specialServices.map((service) => (
              <div key={service.key} className="bg-gray-50 p-2 rounded border text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-800 truncate">
                    {service.name}
                  </span>
                  <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                </div>
                <div className="text-gray-600">
                  {renderUsageText(service.used, service.limit)}
                </div>
              </div>
            ));
          })()}
        </div>
      </div>

      {/* Services without Limits */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Enabled Services</h3>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {allServices.map((service) => {
            const { limit, isEnabled } = getServiceUsage(service.key);
            
            // Only show services without limits that are enabled
            if (limit > 0 || !isEnabled) return null;
            
            return (
              <div key={service.key} className="bg-gray-50 p-2 rounded border text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-800 truncate">
                    {service.name}
                  </span>
                  <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CheckLicenseStatus;